import { useDisplayVariant } from 'modules/productDetail'
import * as React from 'react'
import styled from 'styled-components'
import ArrowLink from 'assets/link-arrow.svg'
import Link from 'theme/atoms/Link'
import config from 'config'
import theme from 'theme'
import useTranslations from 'hooks/useTranslations'
import SpecialLabels from './SpecialLabels'
import useBrandUrl from 'hooks/account/useBrandUrl'
import useWishlist from 'hooks/account/lists/useWishlist'
import HeartEmpty from 'assets/Heart-Empty.svg'
import HeartFilled from 'assets/Heart-Filled.svg'
import Rating from 'theme/molecules/Rating'
import { graphql, useStaticQuery } from 'gatsby'
import slugify from 'utils/slugify'
import Share from 'theme/molecules/Share'

type Props = {
  brandPath: string
}

type Gq = {
  allTagCategory: {
    nodes: {
      tag: string
      urlPath: string
    }[]
  }
}

export default function Title(props: Props) {
  const variant = useDisplayVariant()
  const t = useTranslations<'templates-PDP'>()
  const brandUrl = useBrandUrl(variant.data.brand, props.brandPath)
  const wishlist = useWishlist()
  const listItemId =
    (typeof window !== 'undefined' && window.location.href) || ''

  const gq = useStaticQuery<Gq>(graphql`
    query MyQuery {
      allTagCategory(filter: { active: { eq: true } }) {
        nodes {
          tag
          urlPath
        }
      }
    }
  `)
  const tag = React.useMemo(() => {
    if (!gq.allTagCategory) return
    const slugifiedBrand = slugify(variant.data.brand).replace('/', '')
    return gq.allTagCategory.nodes.find((node) => node.tag === slugifiedBrand)
  }, [variant.data.brand])

  return (
    <Wrapper className="Title">
      <div className="product-info">
        {variant.data.attributes.BRAND &&
          variant.data.attributes.BRAND.values.map((brand, i) => {
            return (
              <Link
                to={tag ? tag.urlPath : brandUrl}
                key={i}
                className="brand"
                data-cy-handle="brand-link"
              >
                <div className="label">{brand?.value}</div>
              </Link>
            )
          })}

        <h1>{variant.data.title}</h1>
        <div className="sub-title">{variant.data.subtitle}</div>

        {variant.data.productReviews && (
          <Rating
            data-cy-state="show-rating"
            containerID={variant.data.containerID}
            ratingAverage={variant.data.productReviews.productReviewAvg}
            ratingCount={variant.data.productReviews.productReviewCount}
            amountText={t('rating_amount_text', {
              COUNT: variant.data.productReviews.productReviewCount || 0
            })}
            clickable
            evtSource="buybox"
          />
        )}

        {variant.data.product_line && variant.data.product_line?.length > 0 && (
          <div className="series-link" data-cy-state="show-series-link">
            <Link to={`/series/${variant.data.product_line[0]}/`}>
              <ArrowLink />
              {t('series_link', {
                seriesName:
                  variant.data.series && variant.data.series.length
                    ? variant.data.series[0]
                    : ''
              })}
            </Link>
          </div>
        )}
      </div>
      <div className="icons">
        <div
          className="icon-button"
          data-cy-handle="wishlist-toggle"
          onClick={() =>
            wishlist.toggleSku(variant.data.sw6Uuid, {
              variant: variant.data,
              listid: 1,
              listname: variant.data.title,
              item_list_id: listItemId
            })
          }
        >
          {wishlist.sw6uuids.has(variant.data.sw6Uuid) ? (
            <HeartFilled data-cy-state="wishlist-added" />
          ) : (
            <HeartEmpty />
          )}
        </div>
        <div className="icon-button">
          <Share productName={variant.data.title}></Share>
        </div>
        <SpecialLabels />
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  > .product-info {
    > .brand {
      ${theme.ty('r-base')}
      margin-bottom: ${theme.spacing('xs')};
      display: flex;

      > .logo {
        margin-right: ${theme.spacing('xs')};
        margin-top: 0.125rem;
        img {
          max-height: 1.1875rem;
        }
      }
    }

    > h1 {
      ${theme.ty('rc-2xl')}

      margin-bottom: ${theme.spacing('xxs')};
    }

    > .sub-title {
      ${theme.ty('r-s')};
      color: ${theme.colors.shade.b3};
      margin-bottom: ${theme.spacing('xs')};
    }

    > .Rating {
      margin-bottom: ${theme.spacing('xs')};
    }
    > .series-link {
      ${theme.ty('r-base')};
      > a {
        text-decoration: underline;
        &:hover {
          text-decoration: none;
        }

        > svg {
          height: 12px;
          margin-right: ${theme.spacing('xs')};
        }
      }
    }

    > .series-link {
      ${theme.ty('r-base')};
      margin-bottom: ${theme.spacing('xs')};
      > a {
        text-decoration: underline;
        &:hover {
          text-decoration: none;
        }

        > svg {
          height: 12px;
          margin-right: ${theme.spacing('xs')};
        }
      }
    }
  }

  > .icons {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing('xs')};
    align-items: flex-end;

    > .icon-button {
      cursor: pointer;
      border: 1px solid ${theme.colors.shade.b2};
      ${theme.rounding('m')};
      height: fit-content;
      min-width: 40px;
      max-width: 40px;
      min-height: 40px;
      max-height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      > svg {
        height: 20px;
        width: 25px;
        transform: scale(1.4);
      }
    }
  }
`
